import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { ToolHeader, RateLimited } from "@/pages/tools/CatalogAnalyzer";
import AnalysisProgress from "@/components/AnalysisProgress";
import CopyBlock from "@/components/CopyBlock";
import { DownloadSimple, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function KeywordGenerator() {
  const [seed, setSeed] = useState("");
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [rateLimited, setRateLimited] = useState(false);

  useEffect(() => { api.get("/tools/usage").then((r) => setUsage(r.data)); }, []);

  const submit = async () => {
    if (!seed.trim()) { toast.error("Enter a seed keyword or product"); return; }
    setLoading(true); setError(""); setResult(null); setRateLimited(false);
    try {
      const { data } = await api.post("/tools/keyword-generator", { seed });
      setResult(data.result);
      const u = await api.get("/tools/usage"); setUsage(u.data);
    } catch (e) {
      if (e?.response?.status === 429) { setRateLimited(true); setError(apiError(e)); }
      else setError(apiError(e));
    } finally { setLoading(false); }
  };

  const kws = result?.keywords || [];
  const downloadCSV = () => {
    const rows = [["Keyword", "Relevance", "Competition"], ...kws.map((k) => [k.keyword, k.relevance, k.competition])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "keywords.csv";
    a.click();
  };

  const u = usage?.tools?.["keyword-generator"];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      <ToolHeader no="03" title="Keyword Generator"
        subtitle="Enter a product or seed keyword. We return 15–20 ranked keywords with relevance and competition signals."
        used={u?.used ?? 0} remaining={u?.remaining ?? 5} limit={usage?.limit ?? 5} isAdmin={usage?.is_admin} />

      <div className="mt-10 flex flex-col sm:flex-row border border-ink">
        <Input value={seed} onChange={(e) => setSeed(e.target.value)}
          className="h-14 border-0 rounded-none flex-1 focus-visible:ring-0 focus-visible:border-0 bg-white"
          placeholder="e.g. collagen powder for women, stainless water bottle…" data-testid="kw-input" />
        <Button onClick={submit} disabled={loading} data-testid="kw-submit"
          className="h-14 px-6 text-base border-0 rounded-none sm:border-l sm:border-ink">
          {loading ? "Generating…" : <>Generate <ArrowRight className="w-4 h-4 ml-2" weight="bold" /></>}
        </Button>
      </div>

      {error && !rateLimited && (
        <div className="mt-6 border border-alert/30 bg-alert-bg text-alert px-4 py-3 text-sm">{error}</div>
      )}
      {rateLimited && <RateLimited />}

      <AnalysisProgress
        active={loading}
        done={kws.length > 0}
        label="Keyword research"
        stages={[
          { title: "Expanding seed term", ms: 1000 },
          { title: "Scoring relevance + competition", ms: 2500 },
          { title: "Composing backend search-terms string", ms: 1200 },
        ]}
      />

      {result?.backend_search_terms && (
        <div className="mt-8">
          <CopyBlock title="Backend search terms · copy-paste ready"
            value={result.backend_search_terms}
            hint="Paste into Seller Central → Keywords → Search terms"
            charLimit={250}
            tone="accent"
            testid="kw-copy-search-terms" />
        </div>
      )}

      {kws.length > 0 && (
        <div className="mt-10 border border-rule bg-white" data-testid="kw-result">
          <div className="flex items-center justify-between border-b border-rule px-6 py-4">
            <div className="label-mono">{kws.length} keywords</div>
            <Button variant="outline" size="sm" onClick={downloadCSV} data-testid="kw-csv-btn">
              <DownloadSimple className="w-3.5 h-3.5 mr-1.5" /> CSV
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-b border-ink">
                <TableHead className="label-mono w-12">№</TableHead>
                <TableHead className="label-mono">Keyword</TableHead>
                <TableHead className="label-mono w-32">Relevance</TableHead>
                <TableHead className="label-mono w-32">Competition</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {kws.map((k, i) => (
                <TableRow key={`${k.keyword}-${i}`} className="hover:bg-surface-hover">
                  <TableCell className="font-mono tabular text-ink/40">{String(i + 1).padStart(2, "0")}</TableCell>
                  <TableCell className="font-medium">{k.keyword}</TableCell>
                  <TableCell className="font-mono tabular">{k.relevance}</TableCell>
                  <TableCell><Badge variant="outline" className={compStyle(k.competition)}>{k.competition}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function compStyle(c) {
  return c === "high" ? "border-alert/40 text-alert"
    : c === "medium" ? "border-gold/40 text-gold"
    : "border-signal/40 text-signal";
}
